import { CollaborativeDraftEditor } from '@agentic-flow-studio/shared/components/clinic/CollaborativeDraftEditor';
import { ThreadDetailHeader } from '@agentic-flow-studio/shared/components/clinic/ThreadDetailHeader';
import { ThreadMessageList } from '@agentic-flow-studio/shared/components/clinic/ThreadMessageList';
import { ThreadTasksPanel } from '@agentic-flow-studio/shared/components/clinic/ThreadTasksPanel';
import { EmailThreadCommentThread } from '@agentic-flow-studio/shared/components/comments/EmailThreadCommentThread';
import { TaskDetailOverlay } from '@agentic-flow-studio/shared/components/tasks/TaskDetailOverlay';
import { useRecordRecentPage } from '@agentic-flow-studio/shared/hooks/useRecordRecentPage';
import { useSearchPreservingNavigate } from '@agentic-flow-studio/shared/hooks/useSearchPreservingNavigate';
import { useWorkspaceEvent } from '@agentic-flow-studio/shared/hooks/useWorkspacePresence';
import { threadDisplayTitle } from '@agentic-flow-studio/shared/lib/clinic-logic';
import type { User as CommentUser } from '@agentic-flow-studio/shared/types/comments';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useClinicThread,
  useClinicThreadMessages,
  useClinicThreadTasks,
  useResolveClinicThread,
  useWorkspaceMembers,
} from '@/hooks/useClinic';
import { clinicApi } from '@/utils/api';
import { trpc } from '@/utils/trpc';

export default function TriageThreadPage() {
  const { orgId, workspaceId, threadId } = useParams<{
    orgId: string;
    workspaceId: string;
    threadId: string;
  }>();
  const navigate = useSearchPreservingNavigate();
  const [expandedTaskId, setExpandedTaskId] = useState<number | null>(null);

  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const { data: thread, isLoading: isThreadLoading } = useClinicThread(threadId!);

  // Null until the thread resolves, so a half-loaded page never lands in the palette's history.
  useRecordRecentPage(
    thread
      ? {
          key: `thread:${thread.threadId}`,
          type: 'thread',
          // An unanalysed thread has no AI title, so fall back through subject to
          // "(no subject)" — the palette's history must never show a blank row.
          label: threadDisplayTitle(thread),
          description: thread.senderName ?? thread.senderEmail,
        }
      : null
  );
  const { data: messagesData, isLoading: isMessagesLoading } = useClinicThreadMessages(threadId!);
  const { data: tasks = [] } = useClinicThreadTasks(threadId!);
  const { data: members = [] } = useWorkspaceMembers();
  const contactsQuery = trpc.contacts.getAll.useQuery({ limit: 100 });
  const statusesQuery = trpc.taskStatuses.list.useQuery();

  const commentUsers: CommentUser[] = members.map((m) => ({
    id: m.id,
    fullName: m.fullName,
    email: m.email,
  }));
  const currentUser: CommentUser | undefined = meQuery.data
    ? commentUsers.find((u) => u.id === meQuery.data!.uid) || {
        id: meQuery.data.uid,
        fullName: 'Me',
        email: '',
      }
    : undefined;

  const queryClient = useQueryClient();

  const linkContactMutation = trpc.emailThreads.linkContact.useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic', 'thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['clinic', 'threads'] });
    },
  });
  const resolveThreadMutation = useResolveClinicThread();

  /**
   * Re-analyse this thread after a failed or deferred analysis. The row already
   * exists — ingest no longer depends on the model (ADR-0003) — so this fills in
   * the summary in place rather than creating anything.
   */
  const retryAnalysisMutation = useMutation({
    mutationFn: (id: string) => clinicApi.ingestThread(id),
    onSuccess: () => {
      toast.success('Thread analyzed');
      queryClient.invalidateQueries({ queryKey: ['clinic', 'thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['clinic', 'threads'] });
    },
    onError: (err: Error) => toast.error(`Analysis failed: ${err.message}`),
  });

  const parsedWorkspaceId = workspaceId ? parseInt(workspaceId, 10) : null;
  const currentUserId = currentUser?.id ?? null;

  useWorkspaceEvent(parsedWorkspaceId, currentUserId, (event) => {
    if (
      event.type === 'TASK_CREATED' ||
      event.type === 'TASK_UPDATED' ||
      event.type === 'TASK_DELETED'
    ) {
      queryClient.invalidateQueries({ queryKey: ['clinic', 'thread', threadId, 'tasks'] });
    }
  });

  const backToTriage = () => navigate(`/org/${orgId}/workspace/${workspaceId}/triage`);

  if (isThreadLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Loading thread...
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3">
        <p className="text-sm text-muted-foreground">Thread not found.</p>
        <button
          onClick={backToTriage}
          className="text-sm text-primary font-semibold hover:underline"
        >
          Back to Triage
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-3xl mx-auto px-8 py-6 space-y-6">
          {isReadOnly && (
            <div className="bg-muted-foreground/10 border border-border text-foreground p-4 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold">Read-only Workspace</p>
                <p className="opacity-90">
                  You have read-only access to this workspace. Modifying tasks and linking contacts
                  is disabled.
                </p>
              </div>
            </div>
          )}

          <ThreadDetailHeader
            thread={thread}
            workspaceMembers={members}
            onBack={backToTriage}
            onRetryAnalysis={(id) => retryAnalysisMutation.mutate(id)}
            isReadOnly={isReadOnly}
            isTogglingResolved={resolveThreadMutation.isPending}
            onToggleResolved={() => {
              if (!threadId) return;
              const resolved = !thread.resolvedAt;
              resolveThreadMutation.mutate(
                { threadId, resolved },
                {
                  onSuccess: () =>
                    toast.success(resolved ? 'Thread marked resolved' : 'Thread reopened'),
                  onError: (err) => toast.error(`Failed to update thread: ${err.message}`),
                }
              );
            }}
          />

          <ThreadMessageList messages={messagesData?.messages} isLoading={isMessagesLoading} />

          <div className="pt-4 border-t">
            <CollaborativeDraftEditor
              threadId={threadId!}
              workspaceMembers={members}
              isReadOnly={isReadOnly}
              accountEmail={thread.accountEmail}
            />
          </div>
        </div>
      </div>

      <aside className="w-[380px] shrink-0 border-l overflow-y-auto scrollbar-thin bg-card/30">
        <ThreadTasksPanel
          thread={thread}
          tasks={tasks}
          statuses={statusesQuery.data?.items ?? []}
          workspaceContacts={contactsQuery.data || []}
          workspaceMembers={members}
          isReadOnly={isReadOnly}
          threadId={threadId ?? ''}
          onTaskCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['clinic'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
          onTaskClick={(taskId) => setExpandedTaskId(taskId)}
          onLinkContact={(contactId) => {
            if (!threadId) return;
            linkContactMutation.mutate(
              { threadId, contactId },
              {
                onSuccess: () => toast.success(contactId ? 'Contact linked' : 'Contact unlinked'),
                onError: (err) => toast.error(`Failed to link contact: ${err.message}`),
              }
            );
          }}
          getContactUrl={(contactId) =>
            `/org/${orgId}/workspace/${workspaceId}/contacts/${contactId}`
          }
        />

        <div className="px-5 pb-5 pt-4 border-t space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground">Discussion</h4>
          {currentUser && (
            <EmailThreadCommentThread
              emailThreadId={threadId!}
              users={commentUsers}
              currentUser={currentUser}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
      </aside>

      {expandedTaskId && (
        <TaskDetailOverlay
          taskId={expandedTaskId}
          workspaceMembers={members}
          isReadOnly={isReadOnly}
          onClose={() => setExpandedTaskId(null)}
        />
      )}
    </div>
  );
}
